import * as React from "react";
import { makeCancelable, CancelablePromiseT } from "../async";

export type CancelableHookT<R = unknown> = {
  promise: CancelablePromiseT<R> | null;
  wrap: (promise: Promise<R> | CancelablePromiseT<R>) => CancelablePromiseT<R>;
  cancel(): void;
};

// This is a utility hook for holding refs to and canceling ongoing request on component did mount
// This is useful for when you want to trigger fetching of something within a user action (like a button click handler) and then set state afterwards
// You can use this hook to make your request cancelable and have it auto-cancelled on unmount.
// Additionally if you call it while another request is already underway it will automatically cancel that request
// so it's good for a search filter form where the user might update several filters in sequence but you only want the latest to be the one that updates state
export function useCancelable<R = unknown>(): CancelableHookT<R> {
  const ref = React.useRef<CancelableHookT<R>>({
    promise: null,
    wrap(p: Promise<R> | CancelablePromiseT<R>): CancelablePromiseT<R> {
      ref.current.promise?.cancel();
      ref.current.promise = "cancel" in p ? p : makeCancelable<R>(p);
      return ref.current.promise;
    },
    cancel() {
      ref.current.promise?.cancel();
      ref.current.promise = null;
    },
  });
  React.useEffect(() => ref.current.cancel, []);
  return ref.current;
}
